import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Icon from "../../../components/base/Icon";
import Indicator from "../../../components/base/Indicator";
import { toastError, toastSuccess } from "../../../components/base/Toast";
import TextArea from "../../../components/form/TextArea";
import { toPrettyDate, toShortDateWithoutTime, useHub } from "../../../core/Global";
import User from "../models/User";
import ToggleSwitch from "../../../components/form/ToggleSwitch";
import TextBox from "../../../components/form/TextBox";
import CustomPlanFields, { ADDONS, AddonPrices, CustomPlanForm, customMonthly, customTerms, parseNumber } from "../../migration/components/CustomPlanFields";
import "../../migration/views/MigrationView.css";
import "./AccountPlan.css";


interface CatalogPlan {
    code: string
    name: string
    monthlyPrice: number
    annualPrice: number
    users: number
    messages: number
    minutes: number
    numbers: number
    messageRate: number
    minuteRate: number
    numberPrice: number
    whatsApp: boolean
    telegram: boolean
    maskedJobCalls: boolean
}

interface PlanTerms {
    planCode?: string
    planName?: string
    price: number
    customPrice: boolean
    users: number
    messages: number
    minutes: number
    numbers: number
    messageRate: number
    minuteRate: number
    numberPrice: number
    whatsApp: "included" | "paid" | "off"
    telegram: "included" | "paid" | "off"
    maskedJobCalls: "included" | "paid" | "off"
}

interface PlanView {
    isPlan: boolean
    isLegacy: boolean
    current?: any
    terms?: PlanTerms
    billing?: string
    messagesUsed: number
    minutesUsed: number
    signedInNow: number
    numbersHeld: number
    extraUsers: number
    plans: CatalogPlan[]
    prices?: AddonPrices
    history: { editedAt: string, editedBy?: string, note?: string, lines: string[] }[]
    status?: string
    /** Charging now or a later start is possible: active, nothing else waiting. */
    canSchedule?: boolean
    renewsAt?: string
    /** The unused part of the period they are in, at what they pay now. */
    unusedCredit: number
    hasCard?: boolean
    scheduled?: {
        effectiveAt: string
        chargeAtStart: boolean
        creditUnused: boolean
        status: "pending" | "applied" | "failed"
        error?: string
        doneAt?: string
        lines: string[]
    }
}

type StartMode = "now" | "hour" | "days" | "renewal"


const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 })
const count = new Intl.NumberFormat("en-US")

const formOf = (t: PlanTerms): CustomPlanForm => ({
    price: String(t.price),
    users: String(t.users),
    messages: String(t.messages),
    minutes: String(t.minutes),
    numbers: String(t.numbers),
    messageRate: String(t.messageRate),
    minuteRate: String(t.minuteRate),
    numberPrice: String(t.numberPrice),
    whatsApp: t.whatsApp,
    telegram: t.telegram,
    maskedJobCalls: t.maskedJobCalls
})

// The catalog plan's figures, the price aside -- the same test the hub makes before keeping a
// catalog plan; anything else set apart from it is saved as the customer's own plan.
const matches = (p: CatalogPlan, f: CustomPlanForm) => {
    const same = (a: number, b: string) => Math.abs(a - parseNumber(b)) < 0.00005
    return same(p.users, f.users) && same(p.messages, f.messages) && same(p.minutes, f.minutes) && same(p.numbers, f.numbers)
        && same(p.messageRate, f.messageRate) && same(p.minuteRate, f.minuteRate) && same(p.numberPrice, f.numberPrice)
        && p.whatsApp === (f.whatsApp === "included")
        && p.telegram === (f.telegram === "included")
        && p.maskedJobCalls === (f.maskedJobCalls === "included")
}


// The Plan tab on a customer's page, for root: what they are on, and every figure of it set by
// hand -- and when the new plan starts and how it is paid for: now or on a later moment, charged
// when it starts (an invoice, and billed from then on) or at the renewal already due.
export default function AccountPlan(props: { model: User }) {

    const hub = useHub()
    const [view, viewSet] = useState<PlanView>()
    const [loading, loadingSet] = useState<boolean>(true)
    const [planCode, planCodeSet] = useState<string>("custom")
    const [form, formSet] = useState<CustomPlanForm>()
    const [note, noteSet] = useState<string>("")
    const [saving, savingSet] = useState<boolean>(false)

    // When the new plan starts, and whether it is paid for then or at the renewal already due.
    const [startMode, startModeSet] = useState<StartMode>("now")
    const [startDays, startDaysSet] = useState<string>("1")
    const [chargeAtStart, chargeAtStartSet] = useState<boolean>(false)
    const [credit, creditSet] = useState<boolean>(true)

    useEffect(() => {
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.model.id])

    function load() {
        loadingSet(true)
        hub.Get("/system/customerplan/" + props.model.id, res => {
            if (res.success) {
                const v: PlanView = res.data
                viewSet(v)
                if (v.terms) {
                    formSet(formOf(v.terms))
                    planCodeSet(v.terms.planCode ?? "custom")
                }
                noteSet("")
                startModeSet("now")
                chargeAtStartSet(false)
            }
            else toastError(res.message ?? "Couldn't load the plan.")
            loadingSet(false)
        }, () => loadingSet(false))
    }

    const annual = view?.billing === "annual"
    const period = annual ? "year" : "month"
    const current = view?.current
    const selected = view?.plans.find(p => p.code === planCode)

    // Saved as: the chosen catalog plan while every figure but the price is its own, else Custom.
    const effectiveCode = selected && form && matches(selected, form) ? selected.code : "custom"
    const madeCustom = !!selected && effectiveCode === "custom"

    const changes = (() => {
        if (!view?.terms || !form) return [] as string[]
        const t = view.terms
        const before = formOf(t)
        const list: string[] = []
        const label: Record<string, string> = {
            price: "Price", users: "Users at once", messages: "Messages included", minutes: "Minutes included",
            numbers: "Numbers included", messageRate: "Each message beyond", minuteRate: "Each minute beyond",
            numberPrice: "Each extra number", whatsApp: "WhatsApp", telegram: "Telegram", maskedJobCalls: "Masked Job Calls"
        }
        if ((t.planCode ?? "custom") !== effectiveCode)
            list.push(`Plan: ${t.planName} → ${effectiveCode === "custom" ? "Custom" : selected?.name}`)
        Object.keys(label).forEach(k => {
            const a = (before as any)[k], b = (form as any)[k]
            const numeric = !["whatsApp", "telegram", "maskedJobCalls"].includes(k)
            const differs = numeric ? Math.abs(parseNumber(a) - parseNumber(b)) > 0.00005 : a !== b
            if (differs) list.push(`${label[k]}: ${a} → ${b}`)
        })
        return list
    })()

    // ---- when it starts and what is charged
    const renewsAt = view?.renewsAt ? new Date(view.renewsAt) : undefined
    const days = Number.parseInt(startDays.replace(/,/g, ""))
    const startValid = startMode !== "days" || (!Number.isNaN(days) && days >= 1 && days <= 365)
    const startsAt: Date | null =
        startMode === "hour" ? new Date(Date.now() + 3600000)
            : startMode === "days" ? (startValid ? new Date(Date.now() + days * 86400000) : null)
                : startMode === "renewal" ? (renewsAt ?? null)
                    : null
    // Starting at the renewal is the renewal: paid for when it starts.
    const charging = startMode === "renewal" || chargeAtStart
    const timed = startMode !== "now" || charging
    const soldPrice = form && view?.terms
        ? ADDONS.filter(a => form[a.key] === "paid" && view.terms![a.key] !== "paid").reduce((s, a) => s + (view.prices?.[a.key] ?? 0), 0)
        : 0
    const newPrice = form ? parseNumber(form.price) || 0 : 0
    // The credit is worked out at the moment it is charged; now it is known exactly.
    const creditNow = charging && credit && startMode === "now" ? (view?.unusedCredit ?? 0) : 0
    const chargeNow = Math.max(0, Math.round((newPrice + soldPrice - creditNow) * 100) / 100)
    const addPeriod = (d: Date) => { const x = new Date(d); if (annual) x.setFullYear(x.getFullYear() + 1); else x.setMonth(x.getMonth() + 1); return x }
    const nextBilling = charging ? addPeriod(startsAt ?? new Date()) : renewsAt
    const startText = startsAt ? toPrettyDate(startsAt as any) : "Now"

    const newUsers = form ? parseNumber(form.users) : 0
    const newNumbers = form ? parseNumber(form.numbers) : 0
    const fromThen = form ? customMonthly(form, view?.prices) : 0

    const choosePlan = (code: string) => {
        planCodeSet(code)
        const p = view?.plans.find(x => x.code === code)
        if (!p || !form) return

        // The catalog plan's figures; what it does not include keeps the choice already made,
        // except one it was including, which is off until sold.
        const addon = (included: boolean, now: "included" | "paid" | "off") =>
            included ? "included" : now === "included" ? "off" : now

        formSet({
            price: String(annual ? p.annualPrice : p.monthlyPrice),
            users: String(p.users),
            messages: String(p.messages),
            minutes: String(p.minutes),
            numbers: String(p.numbers),
            messageRate: String(p.messageRate),
            minuteRate: String(p.minuteRate),
            numberPrice: String(p.numberPrice),
            whatsApp: addon(p.whatsApp, form.whatsApp),
            telegram: addon(p.telegram, form.telegram),
            maskedJobCalls: addon(p.maskedJobCalls, form.maskedJobCalls)
        })
    }

    const reset = () => {
        if (!view?.terms) return
        formSet(formOf(view.terms))
        planCodeSet(view.terms.planCode ?? "custom")
        noteSet("")
    }

    const save = () => {
        if (!form) return
        savingSet(true)
        hub.Post("/system/customerplan/" + props.model.id, {
            planCode: effectiveCode,
            terms: customTerms(form),
            note,
            startsAt: startsAt ? startsAt.toISOString() : null,
            chargeAtStart: charging,
            creditUnused: charging && credit
        }, res => {
            savingSet(false)
            if (res.success) {
                const who = props.model.fullName ?? props.model.username
                const renews = res.data?.renewsAt ? toShortDateWithoutTime(new Date(res.data.renewsAt)) : ""
                toastSuccess(res.data?.scheduledFor
                    ? `Scheduled — ${who} moves to ${res.data.planName} on ${toPrettyDate(new Date(res.data.scheduledFor) as any)}.`
                    : res.data?.charged > 0
                        ? `Charged ${money.format(res.data.charged)} — ${who} is on ${res.data.planName}, renewing ${renews}.`
                        : res.data?.changes > 0
                            ? `Saved — ${who} is on ${res.data.planName}.`
                            : "Nothing was different.")
                load()
            }
            else toastError(res.message ?? "Couldn't save the plan.")
        }, () => savingSet(false))
    }

    const cancelScheduled = () => {
        hub.Post("/system/customerplan/" + props.model.id + "/cancelscheduled", {}, res => {
            if (res.success) { toastSuccess("The scheduled change was cancelled."); load() }
            else toastError(res.message ?? "Couldn't cancel it.")
        })
    }

    if (loading && !view) return <div className="py-20"><Indicator /></div>

    return (
        <div className="account-plan">

            <div className="card mb-5">
                <div className="card-header border-0">
                    <div className="card-title m-0">
                        <h3 className="fw-bolder m-0">Plan</h3>
                    </div>
                </div>
                <div className="card-body border-top px-9 py-8">
                    {
                        view?.isPlan && current ?
                            <>
                                <div className="account-plan-head">
                                    <div>
                                        <div className="account-plan-name">
                                            {current.planName}
                                            {current.planCode === "custom" && <span className="badge badge-light-warning ms-2">own plan</span>}
                                            {current.planCode !== "custom" && view.terms?.customPrice && <span className="badge badge-light-warning ms-2">custom price</span>}
                                        </div>
                                        <div className="text-gray-600 fs-7 mt-1">
                                            {current.isFree ? "Free" : `${money.format(current.price)} / ${period}`}
                                            <span className="mx-2">·</span>
                                            {annual ? "Billed yearly" : "Billed monthly"}
                                        </div>
                                    </div>
                                    <div className="text-end">
                                        <span className={"badge " + (current.isActive && !current.isCanceled && !current.paymentIssue ? "badge-light-success" : "badge-light-danger")}>
                                            {current.statusText}
                                        </span>
                                        <div className="text-gray-600 fs-7 mt-1">
                                            {current.isCanceled ? "Ends" : "Renews"} {toShortDateWithoutTime(current.periodEnd)}
                                        </div>
                                    </div>
                                </div>

                                <div className="account-plan-usage">
                                    <div>
                                        <b>{view.signedInNow} / {(view.terms?.users ?? 0) + view.extraUsers}</b>
                                        <span>signed in now{view.extraUsers > 0 ? ` (${view.extraUsers} extra users)` : ""}</span>
                                    </div>
                                    <div>
                                        <b>{count.format(view.messagesUsed)} / {count.format(view.terms?.messages ?? 0)}</b>
                                        <span>messages this period</span>
                                    </div>
                                    <div>
                                        <b>{count.format(view.minutesUsed)} / {count.format(view.terms?.minutes ?? 0)}</b>
                                        <span>minutes this period</span>
                                    </div>
                                    <div>
                                        <b>{view.numbersHeld} / {view.terms?.numbers ?? 0}</b>
                                        <span>phone numbers</span>
                                    </div>
                                </div>

                                {
                                    current.pendingKind &&
                                    <div className="migration-warn mt-5">
                                        <Icon icon="clock" type="solid" size={13} />
                                        <div>
                                            {current.pendingKind === "hold"
                                                ? `The customer asked for a ${current.pendingHoldMonths}-month pause from ${toShortDateWithoutTime(current.pendingEffectiveAt)}.`
                                                : `The customer asked to move to ${current.pendingPlanName} on ${toShortDateWithoutTime(current.pendingEffectiveAt)}.`}
                                            {" "}Saving here does not touch it.
                                        </div>
                                    </div>
                                }
                            </>
                            : view?.isLegacy && current ?
                                <div className="account-plan-head">
                                    <div>
                                        <div className="account-plan-name">
                                            Old subscription
                                            <span className="badge badge-light-warning ms-2">not migrated</span>
                                        </div>
                                        <div className="text-gray-600 fs-7 mt-1">
                                            {current.priceText} {current.periodText} · {current.seatsIncluded} seats · {current.statusText}
                                            {current.periodEnd && <> · ends {toShortDateWithoutTime(current.periodEnd)}</>}
                                        </div>
                                        <div className="text-gray-600 fs-7 mt-3">
                                            Move them onto a plan on the <Link to="/migration">Migration page</Link> first; the plan can be edited here after that.
                                        </div>
                                    </div>
                                </div>
                                :
                                <div className="text-gray-600">This customer has no subscription.</div>
                    }
                </div>
            </div>

            {
                view?.isPlan && form &&
                <div className="card mb-5">
                    <div className="card-header border-0">
                        <div className="card-title m-0">
                            <h3 className="fw-bolder m-0">Edit plan</h3>
                        </div>
                        <div className="card-toolbar gap-2">
                            <button type="button" className="btn btn-sm btn-light" disabled={saving || changes.length === 0}
                                onClick={e => { e.preventDefault(); reset() }}>
                                Reset
                            </button>
                            <button type="button" className="btn btn-sm btn-primary"
                                disabled={saving || changes.length === 0 || !startValid || (timed && !view.canSchedule)}
                                onClick={e => { e.preventDefault(); save() }}>
                                {
                                    saving ?
                                        <><Icon icon="spinner-third" spin /><span className="ms-2">Saving...</span></>
                                        : startMode !== "now" ? <>Schedule change</>
                                            : charging && chargeNow > 0 ? <>Charge {money.format(chargeNow)} and switch</>
                                                : <>Save plan</>
                                }
                            </button>
                        </div>
                    </div>
                    <div className="card-body border-top px-9 py-8">

                        {
                            view.scheduled &&
                            <div className={"migration-warn mb-6" + (view.scheduled.status === "failed" ? " is-danger" : "")}>
                                <Icon icon={view.scheduled.status === "failed" ? "triangle-exclamation" : "clock"} type="solid" size={13} />
                                <div className="flex-grow-1">
                                    {
                                        view.scheduled.status === "failed" ?
                                            <b>A scheduled change could not be applied{view.scheduled.doneAt ? " on " + toPrettyDate(new Date(view.scheduled.doneAt) as any) : ""}: {view.scheduled.error}. The plan was left as it was.</b>
                                            :
                                            <b>
                                                Scheduled for {toPrettyDate(new Date(view.scheduled.effectiveAt) as any)} —
                                                {view.scheduled.chargeAtStart ? " charged when it starts, billed from then on" : " the billing date stays"}
                                                {view.scheduled.chargeAtStart && view.scheduled.creditUnused ? ", less the unused part of the period" : ""}.
                                            </b>
                                    }
                                    {view.scheduled.lines.map(l => <div key={l} className="fs-8">{l}</div>)}
                                    <button type="button" className="btn btn-sm btn-light mt-2"
                                        onClick={e => { e.preventDefault(); cancelScheduled() }}>
                                        {view.scheduled.status === "failed" ? "Dismiss" : "Cancel scheduled change"}
                                    </button>
                                </div>
                            </div>
                        }

                        <div className="fw-bold mb-3">Plan</div>
                        <div className="migration-plans mb-3">
                            {
                                view.plans.map(p =>
                                    <button key={p.code} type="button"
                                        className={"migration-plan" + (p.code === planCode ? " is-selected" : "")}
                                        onClick={e => { e.preventDefault(); choosePlan(p.code) }}>
                                        <div className="migration-plan-name">{p.name}</div>
                                        <div className="migration-plan-price">
                                            {money.format(annual ? p.annualPrice : p.monthlyPrice)}<span>/{period}</span>
                                        </div>
                                        <div className="migration-plan-users">{p.users} users at once</div>
                                    </button>
                                )
                            }
                            <button type="button"
                                className={"migration-plan" + (planCode === "custom" ? " is-selected" : "")}
                                onClick={e => { e.preventDefault(); planCodeSet("custom") }}>
                                <div className="migration-plan-name">Custom</div>
                                <div className="migration-plan-price"><Icon icon="sliders" type="solid" size={14} /></div>
                                <div className="migration-plan-users">Set every figure</div>
                            </button>
                        </div>

                        {
                            madeCustom &&
                            <div className="migration-warn mb-5">
                                <Icon icon="triangle-exclamation" type="solid" size={13} />
                                <div>
                                    Some figures differ from {selected?.name}, so this is saved as <b>Custom</b> — the customer's own plan,
                                    which a later change to {selected?.name} in the catalog never reaches. Only the price can differ and stay on {selected?.name}.
                                </div>
                            </div>
                        }

                        <div className="mb-6 mt-5">
                            <CustomPlanFields form={form} prices={view.prices} onChange={formSet}
                                priceLabel={annual ? "Yearly price" : "Monthly price"}
                                note={"Call recording is on for every account." +
                                    (effectiveCode === "pro" ? " Extra users can still be bought on Pro, up to 15 in all." : " Extra users are sold on Pro only.")} />
                        </div>

                        <TextArea name="planNote" label="Note (optional) — why, kept in the history below" labelUp block rows={2}
                            value={note} onChange={e => noteSet(e.target.value)} />

                        <div className="fw-bold mt-6 mb-3">When it starts</div>
                        <div className="migration-segments mb-3">
                            {
                                ([["now", "Now"], ["hour", "In 1 hour"], ["days", "In days"], ["renewal", "At the renewal"]] as [StartMode, string][]).map(([key, label]) =>
                                    <button key={key} type="button"
                                        className={"migration-segment" + (startMode === key ? " is-active" : "")}
                                        disabled={key !== "now" && !view.canSchedule}
                                        onClick={e => { e.preventDefault(); startModeSet(key) }}>
                                        {label}{key === "renewal" && renewsAt ? ` · ${toShortDateWithoutTime(renewsAt)}` : ""}
                                    </button>
                                )
                            }
                        </div>
                        {
                            startMode === "days" &&
                            <div className="mb-3" style={{ maxWidth: 220 }}>
                                <TextBox name="startDays" label="Days from now (1 to 365)" labelUp block numeric
                                    value={startDays} onTextChanges={v => startDaysSet(v)} />
                            </div>
                        }

                        <div className="fw-bold mt-4 mb-3">How it is paid for</div>
                        {
                            startMode === "renewal" ?
                                <div className="text-gray-600 fs-7 mb-3">At the renewal: the new plan is charged on its own date, and the billing date stays the same.</div>
                                :
                                <div className="migration-segments mb-3">
                                    <button type="button" className={"migration-segment" + (!chargeAtStart ? " is-active" : "")}
                                        onClick={e => { e.preventDefault(); chargeAtStartSet(false) }}>
                                        Keep the billing date{renewsAt ? ` · ${toShortDateWithoutTime(renewsAt)}` : ""}
                                    </button>
                                    <button type="button" className={"migration-segment" + (chargeAtStart ? " is-active" : "")}
                                        disabled={!view.canSchedule}
                                        onClick={e => { e.preventDefault(); chargeAtStartSet(true) }}>
                                        Charge when it starts
                                    </button>
                                </div>
                        }
                        {
                            charging && startMode !== "renewal" &&
                            <ToggleSwitch name="creditUnused" raw small className="mb-3"
                                label={"Take the unused part of the current period off the charge" +
                                    (startMode === "now" && (view.unusedCredit ?? 0) > 0 ? ` (${money.format(view.unusedCredit)})` : " (worked out when it starts)")}
                                checked={credit} onChange={on => creditSet(on)} />
                        }
                        {
                            !view.canSchedule &&
                            <div className="text-gray-500 fs-8 mb-3">This plan is {view.status === "Active" ? "waiting for a downgrade or a pause" : (view.status ?? "").toLowerCase()}: it can only be changed now, keeping its billing date.</div>
                        }

                        <div className="migration-summary mt-4 mb-6">
                            <div className="migration-summary-row">
                                <span>Starts</span>
                                <b>{startText}</b>
                            </div>
                            <div className="migration-summary-row">
                                <span>Charged</span>
                                <b>
                                    {
                                        !charging ? "Nothing at the start"
                                            : startMode === "now" ? (chargeNow > 0 ? `${money.format(chargeNow)} now, on one invoice` : "Nothing — it costs nothing")
                                                : `When it starts${newPrice + soldPrice > 0 ? ` — ${money.format(newPrice + soldPrice)}${credit && startMode !== "renewal" ? ", less the unused part" : ""}` : ""}`
                                    }
                                </b>
                            </div>
                            <div className="migration-summary-row">
                                <span>Next billing date</span>
                                <b>
                                    {nextBilling ? toShortDateWithoutTime(nextBilling) : "—"}
                                    {!charging || startMode === "renewal" ? " — unchanged" : ""}
                                </b>
                            </div>
                            <div className="migration-summary-row">
                                <span>From then</span>
                                <b>{fromThen <= 0 ? "Free" : `${money.format(fromThen)} / ${period}`}</b>
                            </div>
                        </div>
                        {
                            charging && !view.hasCard && newPrice + soldPrice > 0 &&
                            <div className="migration-warn is-danger mb-6">
                                <Icon icon="credit-card" type="solid" size={13} />
                                <div>This customer has no card on file, so the charge will not go through{startMode === "now" ? " and nothing will be changed" : " and the scheduled change will fail"}.</div>
                            </div>
                        }

                        {
                            changes.length > 0 &&
                            <>
                                <div className="fw-bold mb-3">What changes</div>
                                <ul className="migration-notes mb-6">
                                    {changes.map(c => <li key={c}><Icon icon="pen" type="solid" size={10} />{c}</li>)}
                                </ul>
                            </>
                        }

                        <ul className="migration-notes">
                            {
                                startMode === "now" ?
                                    <li><Icon icon="check" type="solid" size={10} />Takes effect as soon as it is saved. {charging ? "A charge that does not go through changes nothing. Usage starts again from zero with the new period." : "Usage counted this period stays as it is."}</li>
                                    : <li><Icon icon="check" type="solid" size={10} />Nothing changes until {startText}; the renewal engine applies it then. You can cancel it here before that.</li>
                            }
                            <li><Icon icon="check" type="solid" size={10} />Included add-ons switch on with the plan. Paid ones switch on with it and are {charging ? "charged on the same invoice" : "first charged at the renewal"}; one they already pay for keeps its own date.</li>
                            <li><Icon icon="check" type="solid" size={10} />An add-on set to Off: if the plan was giving it, it stops now; if they bought it, it runs to the end of what they paid for and is not renewed.</li>
                            <li><Icon icon="circle-info" type="solid" size={10} />Nobody is signed out and the customer is not notified.</li>
                            {
                                newUsers < view.signedInNow &&
                                <li className="text-danger"><Icon icon="triangle-exclamation" type="solid" size={10} />{view.signedInNow} people are signed in now. They stay signed in, but nobody else can sign in until fewer than {newUsers} are.</li>
                            }
                            {
                                view.extraUsers > 0 && effectiveCode !== "pro" &&
                                <li className="text-danger"><Icon icon="triangle-exclamation" type="solid" size={10} />Their {view.extraUsers} extra users only count on Pro: they stop counting now and are not renewed.</li>
                            }
                            {
                                view.numbersHeld > newNumbers &&
                                <li><Icon icon="circle-info" type="solid" size={10} />They hold {view.numbersHeld} numbers: the oldest {newNumbers} are included, the other {view.numbersHeld - newNumbers} cost {money.format(parseNumber(form.numberPrice))} a month each, from each number's own renewal.</li>
                            }
                            {
                                current?.catalogChangeAt &&
                                <li className="text-danger"><Icon icon="triangle-exclamation" type="solid" size={10} />A catalog change is waiting for their renewal on {toShortDateWithoutTime(current.catalogChangeAt)}. Saving here replaces it.</li>
                            }
                        </ul>
                    </div>
                </div>
            }

            {
                view && view.history.length > 0 &&
                <div className="card mb-5">
                    <div className="card-header border-0">
                        <div className="card-title m-0">
                            <h3 className="fw-bolder m-0">History</h3>
                        </div>
                    </div>
                    <div className="card-body border-top px-9 py-6">
                        {
                            view.history.map((h, k) =>
                                <div key={k} className="account-plan-history">
                                    <div className="text-gray-600 fs-8 mb-1">
                                        {toPrettyDate(h.editedAt as any)} · {h.editedBy ?? "root"}
                                    </div>
                                    {h.note && <div className="fs-7 fw-semibold mb-1">{h.note}</div>}
                                    {h.lines.map(l => <div key={l} className="fs-7 text-gray-800">{l}</div>)}
                                </div>
                            )
                        }
                    </div>
                </div>
            }

        </div>
    )
}
